import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../../environments/environment';

// Response interfaces — one per endpoint.

export interface OrganizationStats {
  subscription_count: number;
  pricing_exception_count: number;
}

/** Per-org billing counts used by the organizations list table. Keyed by organization id. */
export type OrganizationStatsMap = Record<number, OrganizationStats | undefined>;

/** Minimum commit + active sub-customer count for MSP/MMSP orgs. */
export interface MinimumCommitResponse {
  month_label: string;
  floor_amount: number;
  currency: string;
  active_sub_customer_count: number;
  last_updated: string;
  /**
   * True when this org holds no reseller contract of its own and the floor is inherited from an
   * ancestor MSP/MMSP (a sub-MSP). The UI shows the value with a subtle "held at {held_by_name}"
   * caption so it reads as the parent's commitment. Absent/false for a contract-holding org.
   */
  inherited?: boolean;
  held_by_id?: number | null;
  held_by_name?: string | null;
}

export interface AvailableProduct {
  id: number;
  product_name: string;
  sku: string;
  billing_element: string;
  base_rate: number;
  currency: string;
}

export interface AvailableProductsResponse {
  products: AvailableProduct[];
}

export type SubscriptionStatus = 'active' | 'scheduled' | 'expired' | 'cancelled';

/** A base service the org's parent MSP can resell — an option in the +Add Subscription form. */
export interface SubscribableProduct {
  product_id: string;
  sku: string;
  display_name: string;
  service_type: string | null;
  base_rate: number;
  currency: string;
}
/** Row origin: Zenskar mirror line vs an admin-entered customer subscription (B9). */
export type SubscriptionSource = 'zenskar' | 'customer_subscription';

export interface ActiveProduct {
  id: number;
  product_name: string;
  sku: string;
  billing_element: string;
  /** Contracted count (negotiated commitment volume). Editable in the UI. */
  contracted_count: number;
  base_rate: number;
  exception_rate: number | null;
  has_pricing_exception: boolean;
  currency: string;
  /** Subscription lifecycle status (B9). Absent on older payloads. */
  status?: SubscriptionStatus;
  /** Origin of the row (B9): Zenskar own-subscription line vs admin-entered customer subscription. */
  source?: SubscriptionSource;
  /** Catalog service grouping (B7), e.g. EDR / XDR / VSS. */
  service_type?: string | null;
  /** True when the row is an overage line (B7). */
  is_overage?: boolean;
  /** Base SKU an overage row merges into (B7). */
  base_sku?: string | null;
}

export interface ActiveProductsResponse {
  products: ActiveProduct[];
}

/** Response from PATCHing a row's contracted_count. */
export interface ActiveProductCountUpdate {
  id: number;
  source: SubscriptionSource;
  contracted_count: number;
}

/** How an org's billing-week count is chosen. Org-level, locally managed, not per-SKU. */
export type BillingBasis = 'contracted' | 'actual' | 'larger_of';

/** Provenance of the resolved billing basis: the org's own override, inherited, or system default. */
export type BillingBasisSource = 'self' | 'inherited' | 'default';

export interface OrganizationBillingSettings {
  nfr: boolean;
  premium_sla: boolean;
  /** The org's own billing basis. `null` = inherit from parent. Editable. */
  billing_basis: BillingBasis | null;
  /** Read-only (response only): the inheritance-resolved basis actually in effect. */
  effective_billing_basis?: BillingBasis;
  /** Read-only (response only): whether `effective_billing_basis` is own/inherited/default. */
  billing_basis_source?: BillingBasisSource;
  /** Read-only (response only): partner name when the basis is inherited, else null. */
  billing_basis_inherited_from?: string | null;
}

@Injectable({ providedIn: 'root' })
export class BillingService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/v1/billing`;

  /** Bulk subscription + pricing-exception counts keyed by org id. Data source: billing PostgreSQL aggregation. */
  public getOrganizationStats(): Observable<OrganizationStatsMap> {
    return this.http.get<OrganizationStatsMap>(`${this.apiUrl}/organization-stats/`);
  }

  /** Current-month floor and active sub-customer count (MSP/MMSP). Data source: billing PostgreSQL + subscription rollup. */
  public getMinimumCommit(organizationId: number): Observable<MinimumCommitResponse> {
    return this.http.get<MinimumCommitResponse>(`${this.apiUrl}/organization/${organizationId}/minimum-commit/`);
  }

  /** Product catalog with base rates available to this MSP/MMSP. Data source: billing PostgreSQL product catalog. */
  public getAvailableProducts(organizationId: number): Observable<AvailableProductsResponse> {
    return this.http.get<AvailableProductsResponse>(`${this.apiUrl}/organization/${organizationId}/available-products/`);
  }

  /** Active products consumed by this org with base/exception rates. Data source: billing PostgreSQL subscriptions + pricing_exceptions join. */
  public getActiveProducts(organizationId: number): Observable<ActiveProductsResponse> {
    return this.http.get<ActiveProductsResponse>(`${this.apiUrl}/organization/${organizationId}/active-products/`);
  }

  /** NFR / Premium SLA + resolved billing basis for the org. Data source: billing PostgreSQL organization_settings. */
  public getBillingSettings(organizationId: number): Observable<OrganizationBillingSettings> {
    return this.http.get<OrganizationBillingSettings>(`${this.apiUrl}/organization/${organizationId}/settings/`);
  }

  /** Persist NFR / Premium SLA / billing basis (send billing_basis: null to inherit). Data source: billing PostgreSQL organization_settings. */
  public updateBillingSettings(
    organizationId: number,
    settings: OrganizationBillingSettings
  ): Observable<OrganizationBillingSettings> {
    return this.http.patch<OrganizationBillingSettings>(
      `${this.apiUrl}/organization/${organizationId}/settings/`,
      settings
    );
  }

  /**
   * Persist a subscription row's contracted count and/or status in one PATCH. `source` (from the
   * active-products row) routes the write on the backend: a Zenskar line updates its
   * ActiveProductOverride (count only); a customer subscription updates its own count and/or
   * status (incl. cancel). Data source: billing PostgreSQL.
   */
  public updateSubscriptionRow(
    organizationId: number,
    rowId: number,
    source: SubscriptionSource,
    changes: { contracted_count?: number; status?: SubscriptionStatus }
  ): Observable<ActiveProductCountUpdate> {
    return this.http.patch<ActiveProductCountUpdate>(
      `${this.apiUrl}/organization/${organizationId}/active-products/${rowId}/`,
      { ...changes, source }
    );
  }

  /** Base services the org's parent MSP can resell (the +Add Subscription dropdown). */
  public getSubscribableProducts(
    organizationId: number
  ): Observable<{ products: SubscribableProduct[] }> {
    return this.http.get<{ products: SubscribableProduct[] }>(
      `${this.apiUrl}/organization/${organizationId}/subscribable-products/`
    );
  }

  /** Create an admin-entered subscription (rate auto-resolved from the parent's rate card). */
  public createSubscription(
    organizationId: number,
    payload: { product_id: string; contracted_count: number }
  ): Observable<{ id: number }> {
    return this.http.post<{ id: number }>(
      `${this.apiUrl}/organization/${organizationId}/subscriptions/`,
      payload
    );
  }
}
